/**
 * Shopify Section Rendering helper
 * Fetches and updates specific sections without full page reload using Shopify's section rendering API
 */

import { globalState } from './utils'

interface SectionConfig {
  sectionId: string
  fetchSelectors: string[]
  targetSelectors: string[]
  baseUrl?: string
  sectionIdFallbacks?: string[]
  onSectionLoad?: (elements: HTMLElement[]) => void
}

interface SectionResponse {
  [key: string]: string
}

export class ShopifySectionHelper {
  private static async fetchSectionHtml(
    sectionIds: string | string[],
    baseUrl?: string
  ): Promise<SectionResponse> {
    try {
      const sectionParam = Array.isArray(sectionIds) ? sectionIds.join(',') : sectionIds
      const base = baseUrl ?? globalState?.routes?.root ?? window.location.origin
      const url = String(base).replace(/\?.*$/, '')
      const separator = url.includes('?') ? '&' : '?'
      const response = await fetch(`${url}${separator}sections=${sectionParam}`)
      if (!response.ok) throw new Error(`Failed to fetch sections: ${sectionParam}`)
      return await response.json()
    } catch (error) {
      console.error(`Error fetching sections ${sectionIds}:`, error)
      throw error
    }
  }

  private static createDOMFromString(htmlString: string): DocumentFragment {
    const template = document.createElement('template')
    template.innerHTML = htmlString.trim()
    return template.content
  }

  /**
   * Fetches a section by ID and updates the DOM based on provided selectors
   * @param config Configuration object for fetching and updating sections
   * @param sectionResponse Optional pre-fetched section response
   * @returns Promise<HTMLElement[]> Array of elements that were fetched from the section
   */
  static async fetchAndUpdateSection(config: SectionConfig, sectionResponse?: SectionResponse): Promise<HTMLElement[]> {
    const { sectionId, fetchSelectors, targetSelectors, baseUrl, sectionIdFallbacks, onSectionLoad } = config

    if (fetchSelectors.length !== targetSelectors.length) {
      throw new Error('The number of fetch selectors must match the number of target selectors')
    }

    try {
      // Use provided section response or fetch if not provided
      const response = sectionResponse || (await this.fetchSectionHtml(sectionId, baseUrl))
      let sectionHtml = response[sectionId]
      if (!sectionHtml && sectionIdFallbacks) {
        const fallbackKey = sectionIdFallbacks.find((k) => response[k])
        if (fallbackKey) sectionHtml = response[fallbackKey]
      }
      if (!sectionHtml) {
        throw new Error(`Section ${sectionId} not found (tried fallbacks: ${sectionIdFallbacks?.join(', ') || 'none'})`)
      }
      const sectionFragment = this.createDOMFromString(sectionHtml)
      const fetchedElements: HTMLElement[] = []

      const replacements: { targetSelector: string; html: string; source: HTMLElement }[] = []
      fetchSelectors.forEach((fetchSelector, index) => {
        const targetSelector = targetSelectors[index]
        const elementToFetch = sectionFragment.querySelector(fetchSelector) as HTMLElement
        if (!elementToFetch) {
          console.warn(`Could not find element with selector "${fetchSelector}" in section ${sectionId}`)
          return
        }
        replacements.push({ targetSelector, html: elementToFetch.outerHTML, source: elementToFetch })
      })

      replacements.forEach(({ targetSelector, html, source }) => {
        const targetElement = document.querySelector(targetSelector) as HTMLElement
        if (!targetElement) {
          console.warn(`Could not find target element with selector "${targetSelector}"`)
          return
        }
        targetElement.outerHTML = html
        fetchedElements.push(source)
      })

      // Call the onSectionLoad callback if provided
      if (onSectionLoad) {
        onSectionLoad(fetchedElements)
      }

      return fetchedElements
    } catch (error) {
      console.error('Error in fetchAndUpdateSection:', error)
      throw error
    }
  }

  /**
   * Fetch multiple sections and update the DOM with a single request
   * @param configs Array of section configurations
   * @returns Promise<Map<string, HTMLElement[]>>
   */
  static async fetchAndUpdateMultipleSections(configs: SectionConfig[]): Promise<Map<string, HTMLElement[]>> {
    const results = new Map<string, HTMLElement[]>()

    // Get all unique section IDs
    const sectionIds = [...new Set(configs.map((config) => config.sectionId))]

    // Fetch all sections in a single request
    const sectionsResponse = await this.fetchSectionHtml(sectionIds)

    // Process each config with the pre-fetched response
    await Promise.all(
      configs.map(async (config) => {
        const elements = await this.fetchAndUpdateSection(config, sectionsResponse)
        results.set(config.sectionId, elements)
      })
    )

    return results
  }
}
